<template>
  <div class="alert">
    <div class="alert__content">
      <div
        class="alert__card"
        :class="[
          `alert__card-${alert.type.toLowerCase()}`,
          alert.timeout === 0 && `alert__card--hidden`,
          alert.timeout === -1 && `alert__card--hidden alert__message--removed`
        ]"
      >
        <div class="alert__title">
          <icon
            v-if="alertIcon"
            :data="alertIcon.data"
            :fill="true"
            class="alert__title-icon"
            :color="alertIcon.color"
          />
          <BaseHeading
            v-if="alert.type === 'ERROR' && !alert.contentTitle?.length"
            level="3"
            class="alert__error-title"
          >
            Unexpected error encountered
          </BaseHeading>

          <template v-else>
            <BaseHeading
              v-for="(message, index) in alert.contentTitle"
              :key="index"
              level="3"
            >
              {{ message.text }}
            </BaseHeading>
          </template>
        </div>
        <div
          v-if="alert.content?.length"
          class="alert__card__content"
        >
          <div
            v-if="alertIcon"
            class="alert__title-icon"
          />
          <div class="alert__card__content-text">
            <template v-if="alert.type === 'ERROR'">
              <BaseSubHeading
                v-if="alert.type === 'ERROR' && alert.hideDetails"
                size="medium"
                weight="500"
                color="monochrome-07"
              >
                Show Details

                <icon
                  data="@icon/chevron.svg"
                  class="alert__error-icon"
                  :class="{ rotated: showError }"
                  width="var(--icon-16)"
                  height="var(--icon-16)"
                  :color="isDark ? 'var(--monochrome-07)' : 'var(--monochrome-06)'"
                  :fill="false"
                  @click="onToggleErrorShow"
                />
              </BaseSubHeading>

              <transition name="error-slide">
                <div v-if="showError || !alert.hideDetails">
                  <BaseSubHeading
                    v-for="(message, index) in alert.content"
                    :key="index"
                    class="alert__error-message"
                    size="medium"
                    weight="400"
                    color="monochrome-07"
                  >
                    {{ message.text }}
                  </BaseSubHeading>

                  <button
                    v-if="alert.alertRespDetail"
                    type="button"
                    class="alert__error-button"
                    @click="sendReport"
                  >
                    <BaseSubHeading
                      size="medium"
                      weight="500"
                      color="accent-03"
                      darkColor="accent-200"
                    >
                      Submit to Support
                    </BaseSubHeading>
                  </button>
                </div>
              </transition>
            </template>

            <template v-else>
              <BaseSubHeading
                v-if="alert.content[0]"
                size="large"
                weight="500"
                :class="['alert-main-text', alert.type === 'SUCCESS' && 'success-text']"
              >
                {{ alert.content[0].text }}
              </BaseSubHeading>

              <BaseSubHeading
                v-for="(message, index) in alert.content.slice(1)"
                :key="index"
                class="alert-main-text"
                weight="400"
                size="medium"
              >
                {{ message.text }}
              </BaseSubHeading>
            </template>
          </div>
        </div>
        <button
          class="alert__close"
          @click="$emit('closeAlert')"
        >
          <icon
            data="@icon/close.svg"
            :fill="false"
            width="var(--icon-24)"
            height="var(--icon-24)"
            color="#81889B"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Prop } from "vue-property-decorator"
import { Action, Getter } from "s-vuex-class"

import { Alert, AlertReport } from "@/models"
import { Actions, Getters } from "@/store"

import AlertText from "./AlertText.vue"

const components = {
  AlertText
}

@Options({
  name: "GLobalAlert",
  components
})
export default class extends Vue {
  @Prop() alert: Alert

  @Getter(Getters.THEME_IS_DARK) readonly isDark: boolean

  @Action(Actions.SUBMIT_ERROR_REPORT) submitErrorReport: (
    alertReport: AlertReport
  ) => Promise<void>

  showError = false

  get alertIcon() {
    const icons = {
      SUCCESS: { data: require("@icon/check.svg"), color: "#4CAB26" },
      ERROR: { data: require("@icon/attention.svg"), color: "#D32F2F" },
      WARNING: { data: require("@icon/warningIcon.svg"), color: "#C6A700" }
    }
    return icons[this.alert.type]
  }

  onToggleErrorShow() {
    this.showError = !this.showError
  }

  mounted() {
    this.alert.cancelTimeout()
  }

  sendReport() {
    if (this.alert.alertRespDetail) {
      const { responseStatus, requestUrl, message, traceId } = this.alert.alertRespDetail
      const pageUrl = this.$route.fullPath
      this.submitErrorReport({
        responseStatus: responseStatus ?? "",
        requestUrl: requestUrl ?? "",
        pageUrl,
        message: message ?? "",
        traceId: traceId ?? ""
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utils/mixins/_flex";

.alert-secondary-text {
  margin-top: var(--step1);
  color: #777;
  font-size: 14px;
  font-weight: normal;
}

.alert__content {
  min-height: 82px;
}

.alert__card__content {
  width: 100%;

  @include flex;

  gap: var(--step2);
}

.alert__card__content .success-text {
  padding-top: var(--step1);
  font-weight: 400;
}

.alert__error-icon {
  cursor: pointer;
  transition: transform 0.25s ease;
}

.alert__error-icon.rotated {
  transform: rotate(180deg);
}

.error-slide-enter-active,
.error-slide-leave-active {
  overflow: hidden;
  transition: all 0.3s ease;
}

.error-slide-enter-from,
.error-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.error-slide-enter-to,
.error-slide-leave-from {
  max-height: 300px;
  opacity: 1;
}

.alert__error-title {
  margin-bottom: var(--step3);
}

.alert__error-message {
  margin-top: var(--step2);
}

.alert__error-button {
  display: flex;
  width: 100%;
  justify-content: end;
  margin-top: var(--step4);
  cursor: pointer;
}

:deep(.alert__error-icon path) {
  fill: none !important;
}

.alert__close {
  position: absolute;
  top: var(--step2);
  right: var(--step2);
  margin-top: 0;
  margin-right: 0;
}

.alert__title-icon {
  width: var(--icon-24);
  height: var(--icon-24);
}
</style>
